import React, { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Bento Grid Component
 * Flexible grid layout component with toggleable items
 * Dynamically adjusts column spans based on available space
 */
const BentoGrid = ({ items = [], onToggleItem, selected = new Set() }) => {
  const gridRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Filter visible items
  const visibleItems = useMemo(
    () => items.filter((i) => selected.size === 0 || selected.has(i.id)),
    [items, selected]
  );

  // Calculate optimal grid columns and item spans based on container width
  const { columns, adjustedItems } = useMemo(() => {
    if (visibleItems.length === 0) {
      return { columns: 12, adjustedItems: visibleItems };
    }

    // Use container width if available, otherwise fallback to window width estimate
    const width = containerWidth > 0 
      ? containerWidth 
      : (typeof window !== 'undefined' ? window.innerWidth * 0.9 : 1200);

    // Base column count - responsive to container width
    // Calculate based on a minimum column width of ~80px (allows more columns for wider layouts)
    const minColumnWidth = 80;
    const gap = 24; // var(--space-2xl) = 24px
    const calculatedColumns = Math.max(
      2,
      Math.min(
        24,
        Math.floor((width + gap) / (minColumnWidth + gap))
      )
    );

    // Limit columns to prevent squishing when many items are visible
    // Always use 12 columns for consistent grid
    let optimalColumns = 12;
    
    // For many items (5+), force full width to stack vertically
    // For fewer items, allow side-by-side layout
    if (visibleItems.length > 4) {
      // Force full width (12 cols) for vertical stacking
      optimalColumns = 12;
    } else {
      // For 1-4 items, use 12 columns but allow side-by-side
      optimalColumns = 12;
    }

    // Adjust item column spans to fit side by side when possible
    const adjustedItems = visibleItems.map((item, index) => {
      const originalCols = item.cols || 1;
      
      // For single item, always use full width
      if (visibleItems.length === 1) {
        return {
          ...item,
          adjustedCols: optimalColumns,
          adjustedRows: item.rows || 1
        };
      }
      
      // Calculate the proportional span based on the original 12-column grid
      const ratio = originalCols / 12;
      let newCols = Math.round(optimalColumns * ratio);
      
      // Ensure minimum and maximum bounds
      newCols = Math.max(1, Math.min(newCols, optimalColumns));
      
      // For multiple items, prevent squishing by ensuring minimum width
      if (visibleItems.length >= 2) {
        // For many items (5+), force full width to stack vertically
        if (visibleItems.length > 4) {
          newCols = optimalColumns; // Full width for vertical stacking
        } else {
          // For 2-4 items, ensure minimum width to prevent squishing
          // Each item should get at least 6 columns (50% of 12)
          const minColsPerItem = 6;
          const maxColsPerItem = Math.floor(optimalColumns / Math.min(visibleItems.length, 2));
          
          // Calculate proportional sizing
          const totalScaledCols = visibleItems.reduce((sum, i) => {
            const origCols = i.cols || 1;
            const ratio = origCols / 12;
            return sum + Math.max(1, Math.min(Math.round(optimalColumns * ratio), optimalColumns));
          }, 0);
          
          if (totalScaledCols <= optimalColumns && visibleItems.length <= 2) {
            // For 2 items, allow proportional sizing but ensure minimum
            newCols = Math.max(newCols, minColsPerItem);
            newCols = Math.min(newCols, maxColsPerItem);
          } else {
            // For 3-4 items or when items don't fit, distribute evenly
            newCols = Math.floor(optimalColumns / Math.min(visibleItems.length, 2));
            newCols = Math.max(newCols, minColsPerItem);
          }
        }
      }
      
      return {
        ...item,
        adjustedCols: newCols,
        adjustedRows: item.rows || 1
      };
    });

    return { columns: optimalColumns, adjustedItems };
  }, [visibleItems, containerWidth]);

  // Measure container width on mount and resize
  useEffect(() => {
    if (!gridRef.current) return;

    const updateWidth = () => {
      if (gridRef.current) {
        setContainerWidth(gridRef.current.offsetWidth);
      }
    };

    // Initial measurement - use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      updateWidth();
    });

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(gridRef.current);

    // Fallback to window resize listener
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <div className="bento-wrapper">
      <div className="bento-controls">
        {items.map((item) => (
          <button
            key={item.id}
            className={`bento-chip ${selected.has(item.id) ? 'active' : ''}`}
            onClick={() => onToggleItem?.(item.id)}
          >
            {item.icon ? <span className="chip-icon">{item.icon}</span> : null}
            {item.label || item.title}
          </button>
        ))}
      </div>

      <div 
        ref={gridRef}
        className="bento-grid"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {adjustedItems.map((item) => (
          <section
            key={item.id}
            className="bento-item"
            style={{ 
              gridColumn: `span ${item.adjustedCols || item.cols || 1}`, 
              gridRow: `span ${item.adjustedRows || item.rows || 1}` 
            }}
          >
            {item.title ? (
              <h2 className="section-title">
                {item.title}
              </h2>
            ) : null}
            <div className="bento-content">
              {item.content}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

BentoGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      title: PropTypes.string,
      cols: PropTypes.number,
      rows: PropTypes.number,
      content: PropTypes.node.isRequired,
      icon: PropTypes.node
    })
  ).isRequired,
  onToggleItem: PropTypes.func,
  selected: PropTypes.instanceOf(Set)
};

export default BentoGrid;


